import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import { toast as alert } from "react-toastify";

import "./settings-modal.css";

import BookkeepingService from "../../../../services/BookkeepingService";
import UtilityService from "../../../../services/UtilityService";

import WComponent from "../../../common/WComponent";
import Checkbox from "../../../common/checkbox/Checkbox";
import ToolsModal from "../kit/ToolsModal";
import ToolsTabs from "../kit/ToolsTabs";
import ToolsSelect from "../kit/ToolsSelect";
import FiscalPeriodFields from "./FiscalPeriodFields";
import CustomTaxRateRow, { CUSTOM_RATE_OPTION_VALUE, COMMON_TAX_NAMES } from "./CustomTaxRateRow";

import TAX_CATEGORIES from "../../../../constants/Taxes";
import CURRENCIES from "../../../../constants/Currencies";

class SettingsModal extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			activeTab: props.initialTab || "accounts",
			searchText: "",
			accountRenameDrafts: {},
			newTaxRateName: "",
			newTaxRatePercent: "",
			editingTaxRate: null,
			newRuleKeyword: "",
			newRuleTax: "",
			periodDraftEnd: (props.fiscalPeriod && props.fiscalPeriod.periodEnd) || "",
			periodDraftStart: (props.fiscalPeriod && props.fiscalPeriod.periodStart) || "",
			periodDraftLengthMonths: props.fiscalPeriod ? BookkeepingService.matchPeriodLengthOption({ fiscalPeriod: props.fiscalPeriod }) : 12,
			periodDraftError: "",
			periodSaving: false,
			earningsClose: null,
			earningsCloseLoading: false,
			earningsCloseDraftId: null,
			earningsCloseSaving: false,
			earningsCloseError: null,
			openingRetainedEarnings: null,
			customRateForAccount: null,
			accountToRetire: null,
			retiring: false,
			taxSetup: null,
			taxSetupLoading: false,
			taxSetupError: null,
		};
	}

	componentDidMount() {
		if (this.state.activeTab === "taxes") this.loadTaxSetupWhenTheBooksAreATrialBalance();
	}

	onTabSelect = async tab => {
		await this.update({ activeTab: tab });
		if (tab === "prior-year" && !this.state.earningsClose) await this.loadEarningsClose();
		if (tab === "taxes") await this.loadTaxSetupWhenTheBooksAreATrialBalance();
	};

	loadTaxSetupWhenTheBooksAreATrialBalance = async () => {
		const { clientId, wpfEngagementId, booksAreATrialBalance } = this.props;
		if (!booksAreATrialBalance || this.state.taxSetup || this.state.taxSetupLoading) return;
		await this.update({ taxSetupLoading: true, taxSetupError: null });
		const answer = await BookkeepingService.describeQuickBooksTaxSetup({ clientId, wpfEngagementId });
		await this.update({ taxSetupLoading: false, taxSetup: answer.taxSetup || null, taxSetupError: answer.error || null });
	};

	loadEarningsClose = async () => {
		const { clientId, wpfEngagementId } = this.props;
		await this.update({ earningsCloseLoading: true, earningsCloseError: null });
		const answer = await BookkeepingService.fetchEarningsClose({ clientId, wpfEngagementId });
		if (answer.error) {
			await this.update({ earningsCloseLoading: false, earningsCloseError: answer.error });
			return;
		}
		const earningsClose = answer.earningsClose;
		await this.update({
			earningsCloseLoading: false,
			earningsClose,
			openingRetainedEarnings: answer.openingRetainedEarnings || null,
			earningsCloseDraftId: earningsClose && earningsClose.designatedAccount
				? earningsClose.designatedAccount.id
				: (earningsClose ? earningsClose.suggestedAccountId : null),
		});
	};

	onSaveEarningsClose = async () => {
		const { wpfEngagementId } = this.props;
		const { earningsCloseDraftId } = this.state;
		if (!earningsCloseDraftId) return;

		await this.update({ earningsCloseSaving: true, earningsCloseError: null });
		const result = await BookkeepingService.designateEarningsCloseAccount({ wpfEngagementId, accountId: earningsCloseDraftId });
		await this.update({ earningsCloseSaving: false });

		if (!result || !result.ok) {
			await this.update({ earningsCloseError: (result && result.reason) || "That account could not be recorded." });
			return;
		}

		await this.update({ earningsClose: null });
		await this.loadEarningsClose();
		alert.success("Last year’s profit now closes into " + result.account.accountNumber + " " + result.account.accountDescription + ".");
	};

	onPeriodDraftChange = ({ periodEnd, periodStart, lengthMonths }) => {
		const changes = { periodDraftError: "" };
		if (periodEnd !== undefined) changes.periodDraftEnd = periodEnd;
		if (periodStart !== undefined) changes.periodDraftStart = periodStart;
		if (lengthMonths !== undefined) changes.periodDraftLengthMonths = lengthMonths;
		this.update(changes);
	};

	onSaveFiscalPeriod = async () => {
		const { periodDraftEnd, periodDraftStart, periodDraftLengthMonths } = this.state;

		const derived = BookkeepingService.derivePeriodFromYearEnd({
			fiscalYearEnd: periodDraftEnd,
			periodLengthMonths: periodDraftLengthMonths,
			periodStartOverride: periodDraftStart,
		});
		if (derived.error) {
			await this.update({ periodDraftError: derived.error });
			return;
		}

		await this.update({ periodSaving: true });
		const result = await this.props.onFiscalPeriodChange({ derivedPeriod: derived });
		await this.update({ periodSaving: false });

		if (result && result.error) {
			await this.update({ periodDraftError: result.error });
			return;
		}
		await this.update({ periodDraftError: "" });
	};

	renderOpeningRetainedEarnings = () => {
		const { openingRetainedEarnings } = this.state;
		if (!openingRetainedEarnings || openingRetainedEarnings.lines.length === 0) return null;

		const SOURCE_LABELS = {
			deliverable: "carried forward from last year’s signed compilation",
			manual: "built from prior-year figures entered by hand",
		};
		const sourceLabel = SOURCE_LABELS[openingRetainedEarnings.source] || "with no prior year on record";

		return (
			<div className="bk-openingre">
				<p className="bk-settings__section-hint">
					Opening retained earnings is the one figure on these statements that cannot be checked against this
					year’s books, because it arrives from last year. This is where it comes from — {sourceLabel}.
				</p>

				<div className="bk-openingre__lines">
					{openingRetainedEarnings.lines.map((line, index) => (
						<div key={index} className="bk-openingre__line">
							<span>{line.description}</span>
							<strong>{UtilityService.formatCurrency(line.amount)}</strong>
						</div>
					))}
					<div className="bk-openingre__line bk-openingre__line--total">
						<span>Opening retained earnings</span>
						<strong>{UtilityService.formatCurrency(openingRetainedEarnings.openingRetainedEarnings)}</strong>
					</div>
				</div>

				{openingRetainedEarnings.agrees === false && (
					<p className="bk-openingre__disagrees">
						These lines come to {UtilityService.formatCurrency(openingRetainedEarnings.openingRetainedEarnings)}, but the
						opening balance actually carried forward is {UtilityService.formatCurrency(openingRetainedEarnings.derivedTotal)}.
						Something is being counted differently — do not sign off until this is understood.
					</p>
				)}
			</div>
		);
	};

	renderColumnsTab = () => {
		const { hideableColumns, hiddenColumnKeys, onToggleColumn, onResetColumns } = this.props;
		const hiddenCount = (hiddenColumnKeys || []).length;

		return (
			<div className="bk-settings__columns">
				<p className="bk-settings__section-hint">
					Choose which columns to show. Drag a column's right edge in the table to resize it. Both are saved for this client on this computer.
				</p>

				<div className="bk-columns__list">
					{(hideableColumns || []).map(heading => (
						<Checkbox
							key={heading.key}
							containerClass="bk-columns__row"
							title={heading.name}
							checked={!(hiddenColumnKeys || []).includes(heading.key)}
							onChange={() => onToggleColumn(heading.key)}
						/>
					))}
				</div>

				{hiddenCount > 0 && (
					<p className="bk-columns__hidden-note">
						{hiddenCount} column{hiddenCount === 1 ? "" : "s"} hidden. Hidden columns still import, classify and appear in exports — this only changes what you see here.
					</p>
				)}

				<button type="button" className="tk-button tk-button--secondary" onClick={onResetColumns}>Reset to default</button>
			</div>
		);
	};

	onClearEarningsClose = async () => {
		const { wpfEngagementId } = this.props;
		await this.update({ earningsCloseSaving: true, earningsCloseError: null });
		const result = await BookkeepingService.designateEarningsCloseAccount({ wpfEngagementId, accountId: null });
		await this.update({ earningsCloseSaving: false });

		if (!result || !result.ok) {
			await this.update({ earningsCloseError: (result && result.reason) || "That designation could not be cleared." });
			return;
		}

		await this.update({ earningsClose: null });
		await this.loadEarningsClose();
		alert.success("Cleared. The opening balances will be held back until an account is chosen again.");
	};

	renderPriorYearTab = () => {
		const { earningsClose, earningsCloseLoading, earningsCloseDraftId, earningsCloseSaving, earningsCloseError } = this.state;

		if (earningsCloseLoading) return <p className="bk-settings__section-hint">Loading…</p>;
		if (earningsCloseError) return <p className="bk-settings__section-hint">{earningsCloseError}</p>;
		if (!earningsClose) return <p className="bk-settings__section-hint">No prior-year figures have been imported for this engagement yet.</p>;

		const profit = earningsClose.netIncome;
		const isAProfit = profit >= 0;
		const options = earningsClose.equityAccounts.map(account => ({
			value: account.id,
			label: (account.accountNumber ? account.accountNumber + " " : "") + account.accountDescription,
		}));
		const designated = earningsClose.designatedAccount;
		const isUnchanged = !!designated && designated.id === earningsCloseDraftId;

		if (options.length === 0) return <p className="bk-settings__section-hint">This client has no equity account on its chart, so there is nowhere to close the year into.</p>;

		return (
			<div className="bk-settings__prior-year">
				{this.renderOpeningRetainedEarnings()}
				<p className="bk-settings__section-hint">
					The prior year’s {isAProfit ? "profit" : "loss"} closes into equity before it opens this year. Changing the
					account re-works the opening balances; nothing else moves.
				</p>

				<div className="bk-settings__prior-year-figure">
					<span>{isAProfit ? "Profit closing into equity" : "Loss closing into equity"}</span>
					<strong>{UtilityService.formatCurrency(Math.abs(profit))}</strong>
				</div>

				<label className="tk-form-label">Equity account</label>
				<ToolsSelect
					value={earningsCloseDraftId}
					options={options}
					onChange={value => this.update({ earningsCloseDraftId: value })}
					placeholder="Choose the account it closes into…"
				/>
				<p className="tk-form-help">
					{designated
						? "Currently " + designated.accountNumber + " " + designated.accountDescription + "."
						: "Nothing is designated yet, so the opening balances are not being worked out."}
				</p>

				<div className="bk-settings__prior-year-actions">
					<button
						type="button"
						className="tk-button tk-button--secondary"
						onClick={this.onSaveEarningsClose}
						disabled={earningsCloseSaving || isUnchanged || !earningsCloseDraftId}
					>
						{earningsCloseSaving ? "Applying…" : "Apply equity account"}
					</button>
					{designated && (
						<button type="button" className="tk-text-action tk-text-action--muted" onClick={this.onClearEarningsClose} disabled={earningsCloseSaving}>
							Clear
						</button>
					)}
				</div>
			</div>
		);
	};

	renderFiscalPeriodTab = () => {
		const { fiscalPeriod } = this.props;
		const { periodDraftEnd, periodDraftStart, periodDraftLengthMonths, periodDraftError, periodSaving } = this.state;

		const isUnchanged = !!fiscalPeriod && fiscalPeriod.periodEnd === periodDraftEnd && fiscalPeriod.periodStart === periodDraftStart;

		return (
			<div className="bk-settings__period">
				<p className="bk-settings__section-hint">
					Changing the period re-scopes these books: transactions outside it are held aside, and any already on this client's record that fall inside it are brought back in. Nothing is deleted.
				</p>

				<FiscalPeriodFields
					radioGroupName="bk-settings-period-length"
					periodEnd={periodDraftEnd}
					periodStart={periodDraftStart}
					lengthMonths={periodDraftLengthMonths}
					errorText={periodDraftError}
					onDraftChange={this.onPeriodDraftChange}
				/>

				<button
					type="button"
					className="tk-button tk-button--secondary"
					onClick={this.onSaveFiscalPeriod}
					disabled={periodSaving || isUnchanged || !periodDraftEnd}
				>
					{periodSaving ? "Applying…" : "Apply fiscal period"}
				</button>
			</div>
		);
	};

	onAccountRenameDraftChange = (originalName, draft) => {
		this.setState(prev => ({ accountRenameDrafts: { ...prev.accountRenameDrafts, [originalName]: draft } }));
	};

	onApplyAccountRename = async oldName => {
		const { transactions, classifications, classificationTaxMap, clientId, onClassificationsChange, onTaxMapChange, onTransactionsUpdate, saveTransactions, getChartAccountIdForName, getCanonicalCodeForAccount } = this.props;

		const renameDraft = this.state.accountRenameDrafts[oldName] || "";
		const newName = renameDraft.trim();
		if (!newName || newName === oldName) return;

		const nameUsedByClassification = classifications.some(c => c.name === newName);
		const nameUsedByTransaction = (transactions || []).some(t => t.classification === newName);
		const nameAlreadyUsed = nameUsedByClassification || nameUsedByTransaction;
		if (nameAlreadyUsed) {
			alert.error(`"${newName}" already exists — pick a different name to avoid merging accounts.`);
			return;
		}

		const renamedTransactions = BookkeepingService.renameClassificationOnTransactions({ transactions, oldName, newName });
		const renamedClassifications = classifications.map(c => (c.name === oldName ? { ...c, name: newName } : c));

		const updatedTaxMap = { ...classificationTaxMap };
		if (updatedTaxMap[oldName] !== undefined) {
			updatedTaxMap[newName] = updatedTaxMap[oldName];
			delete updatedTaxMap[oldName];
		}
		const serializedTaxMap = JSON.stringify(updatedTaxMap);
		localStorage.setItem(`bookkeeping_tax_map_${clientId}`, serializedTaxMap);

		const savedCustom = JSON.parse(localStorage.getItem(`bookkeeping_custom_classifications_${clientId}`) || "[]");
		const renamedCustom = savedCustom.map(c => (c.name === oldName ? { ...c, name: newName } : c));
		const serializedCustom = JSON.stringify(renamedCustom);
		localStorage.setItem(`bookkeeping_custom_classifications_${clientId}`, serializedCustom);

		const chartAccountId = getChartAccountIdForName(oldName);
		const canonicalCode = getCanonicalCodeForAccount(oldName);
		if (chartAccountId && canonicalCode) {
			try {
				await BookkeepingService.mapCoaSave({
					clientId,
					mappings: [{ accountId: chartAccountId, accountName: newName, canonical_code: canonicalCode }],
				});
			} catch (_) {}
		}

		const accountRenameDrafts = UtilityService.omitKey({ object: this.state.accountRenameDrafts, key: oldName });

		await this.update({ accountRenameDrafts });
		onClassificationsChange(renamedClassifications);
		onTaxMapChange(updatedTaxMap);
		onTransactionsUpdate(renamedTransactions);
		await saveTransactions(renamedTransactions);
		alert.success(`Renamed "${oldName}" to "${newName}".`);
	};

	onRemapAccountToCode = (classificationName, canonicalCode) => {
		const { canonicalTaxonomyOptions } = this.props;
		const canonicalNode = (canonicalTaxonomyOptions || []).find(node => node.canonical_code === canonicalCode);
		if (canonicalNode) this.onRemapAccount(classificationName, canonicalNode);
	};

	onRemapAccount = async (classificationName, canonicalNode) => {
		const { transactions, clientId, onTransactionsUpdate, saveTransactions, getChartAccountIdForName } = this.props;

		const remappedTransactions = BookkeepingService.remapTransactionsToCanonical({ transactions, classificationName, canonicalNode });
		onTransactionsUpdate(remappedTransactions);

		try {
			await BookkeepingService.mapCoaSave({
				clientId,
				mappings: [{ accountId: getChartAccountIdForName(classificationName), accountNumber: BookkeepingService.getLeadingAccountNumber({ accountName: classificationName }), accountName: classificationName, canonical_code: canonicalNode.canonical_code, source: "manual" }],
			});
		} catch (_) {}

		await saveTransactions(remappedTransactions);
		alert.success(`"${classificationName}" mapped to ${canonicalNode.display_name || canonicalNode.name}.`);
	};

	setAccountDefaultTax = async (classificationName, tax) => {
		const { clientId, transactions, classificationTaxMap, onTaxMapChange, onTransactionsUpdate } = this.props;

		const updatedMap = { ...classificationTaxMap, [classificationName]: tax };
		const serializedTaxMap = JSON.stringify(updatedMap);
		localStorage.setItem(`bookkeeping_tax_map_${clientId}`, serializedTaxMap);

		onTaxMapChange(updatedMap);

		const updatedTransactions = transactions.map(t =>
			t.classification === classificationName ? { ...t, tax } : t
		);
		onTransactionsUpdate(updatedTransactions);
	};

	onAccountTaxSelect = (accountName, selectedValue) => {
		if (selectedValue === CUSTOM_RATE_OPTION_VALUE) {
			this.update({ customRateForAccount: accountName });
			return;
		}
		this.setAccountDefaultTax(accountName, selectedValue);
	};

	onCustomRateAppliedToAccount = async ({ option }) => {
		const accountName = this.state.customRateForAccount;

		await this.update({ customRateForAccount: null });
		await this.setAccountDefaultTax(accountName, option);
		alert.success(`Applied "${option}" to ${accountName}.`);
	};

	onCancelCustomRateForAccount = () => this.update({ customRateForAccount: null });

	onAddCustomTaxRate = async () => {
		const { newTaxRateName, newTaxRatePercent, editingTaxRate } = this.state;

		const built = BookkeepingService.buildCustomTaxRateOption({ name: newTaxRateName.trim(), ratePercent: newTaxRatePercent });
		if (built.error) {
			alert.error(built.error);
			return;
		}

		if (editingTaxRate) {
			await this.updateExistingTaxRate(built.option);
		} else {
			await this.addNewTaxRate(built.option);
		}
	};

	updateExistingTaxRate = async taxOption => {
		const { clientId, customTaxRates, taxOptions, onCustomTaxRatesChange } = this.props;
		const { editingTaxRate } = this.state;

		if (taxOption !== editingTaxRate && taxOptions.includes(taxOption)) {
			alert.error(`"${taxOption}" already exists.`);
			return;
		}

		const updatedRates = customTaxRates.map(rate => (rate === editingTaxRate ? taxOption : rate));
		BookkeepingService.saveCustomTaxRates({ clientId, customTaxRates: updatedRates });
		onCustomTaxRatesChange(updatedRates);
		await this.update({ newTaxRateName: "", newTaxRatePercent: "", editingTaxRate: null });

		if (taxOption !== editingTaxRate) {
			await this.applyTaxRateChangeEverywhere(editingTaxRate, taxOption);
			alert.success(`Updated "${editingTaxRate}" to "${taxOption}".`);
		}
	};

	addNewTaxRate = async taxOption => {
		const { clientId, customTaxRates, taxOptions, onCustomTaxRatesChange } = this.props;

		if (taxOptions.includes(taxOption)) {
			alert.error(`"${taxOption}" already exists.`);
			return;
		}

		const updatedRates = [...customTaxRates, taxOption];
		BookkeepingService.saveCustomTaxRates({ clientId, customTaxRates: updatedRates });
		onCustomTaxRatesChange(updatedRates);
		await this.update({ newTaxRateName: "", newTaxRatePercent: "" });
		alert.success(`Added "${taxOption}".`);
	};

	applyTaxRateChangeEverywhere = async (oldTaxCode, newTaxCode) => {
		const { clientId, transactions, classificationTaxMap, onTaxMapChange, onTransactionsUpdate, saveTransactions } = this.props;

		const updatedTransactions = BookkeepingService.replaceTaxCodeOnTransactions({ transactions, oldTaxCode, newTaxCode });
		onTransactionsUpdate(updatedTransactions);

		const updatedTaxMap = UtilityService.replaceValueInMap({ object: classificationTaxMap, oldValue: oldTaxCode, newValue: newTaxCode });
		const serializedTaxMap = JSON.stringify(updatedTaxMap);
		localStorage.setItem(`bookkeeping_tax_map_${clientId}`, serializedTaxMap);
		onTaxMapChange(updatedTaxMap);

		await saveTransactions(updatedTransactions);
	};

	onStartEditingTaxRate = taxOption => {
		const { name, ratePercent } = BookkeepingService.parseCustomTaxRateOption({ option: taxOption });
		this.update({ editingTaxRate: taxOption, newTaxRateName: name, newTaxRatePercent: ratePercent });
	};

	onCancelEditingTaxRate = () => {
		this.update({ editingTaxRate: null, newTaxRateName: "", newTaxRatePercent: "" });
	};

	onRemoveCustomTaxRate = async taxOption => {
		const { clientId, customTaxRates, onCustomTaxRatesChange } = this.props;

		const updatedRates = customTaxRates.filter(rate => rate !== taxOption);
		BookkeepingService.saveCustomTaxRates({ clientId, customTaxRates: updatedRates });
		onCustomTaxRatesChange(updatedRates);
	};

	renderAccountRow = accountName => {
		const { canonicalTaxonomyOptions, getCanonicalLabelForAccount } = this.props;
		const { accountRenameDrafts } = this.state;

		const draft = accountRenameDrafts[accountName] !== undefined ? accountRenameDrafts[accountName] : accountName;
		const trimmedDraft = draft.trim();
		const renamePending = trimmedDraft !== "" && trimmedDraft !== accountName;
		const taxonomyOptions = canonicalTaxonomyOptions || [];
		const canonicalChoices = taxonomyOptions.map(node => ({
			value: node.canonical_code,
			label: node.display_name,
			hint: BookkeepingService.whichStatementAnAccountSitsOn({ statementType: node.statement_type }),
		}));

		const currentLabel = getCanonicalLabelForAccount(accountName);
		const currentChoice = canonicalChoices.find(choice => choice.label === currentLabel);
		const currentValue = currentChoice ? currentChoice.value : "";

		return (
			<div key={accountName} className="bk-settings__account-row">
				<div className="bk-settings__account-name">
					<input value={draft} onChange={e => this.onAccountRenameDraftChange(accountName, e.target.value)} />
					{renamePending && (
						<button className="bk-settings__rename-apply" title="Apply rename" onClick={() => this.onApplyAccountRename(accountName)}>
							<Icon.Check size={14} />
						</button>
					)}
				</div>
				<div className="bk-settings__account-fs">
					<ToolsSelect
						value={currentValue}
						options={canonicalChoices}
						onChange={canonicalCode => this.onRemapAccountToCode(accountName, canonicalCode)}
						placeholder="Map to FS account…"
						searchable
						searchPlaceholder="Search FS accounts…"
					/>
				</div>
				{this.renderRetireAction(accountName)}
			</div>
		);
	};

	renderRetireAction = accountName => {
		const { getChartAccountIdForName, onRetireAccount } = this.props;
		if (!onRetireAccount || !getChartAccountIdForName) return null;

		const accountId = getChartAccountIdForName(accountName);
		if (!accountId) return null;

		return (
			<button
				type="button"
				className="bk-settings__account-retire"
				title="Put this account away"
				onClick={() => this.update({ accountToRetire: { accountName, accountId } })}
			>
				<Icon.Archive size={14} />
			</button>
		);
	};

	onConfirmRetire = async () => {
		const { onRetireAccount } = this.props;
		const { accountToRetire } = this.state;
		if (!accountToRetire) return;

		await this.update({ retiring: true });
		const outcome = await onRetireAccount(accountToRetire);
		await this.update({ retiring: false });

		const itWasRefused = outcome && outcome.retired === false;
		if (itWasRefused) return;
		await this.update({ accountToRetire: null });
	};

	renderRetireConfirmation = () => {
		const { accountToRetire, retiring } = this.state;
		if (!accountToRetire) return null;

		const footer = (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" disabled={retiring} onClick={() => this.update({ accountToRetire: null })}>Cancel</button>
					<button type="button" className="tk-button tk-button--primary" disabled={retiring} onClick={this.onConfirmRetire}>
						{retiring ? "Putting it away…" : "Put it away"}
					</button>
				</span>
			</>
		);

		return (
			<ToolsModal open={true} onClose={() => this.update({ accountToRetire: null })} title={`Put ${accountToRetire.accountName} away?`} width={520} footer={footer}>
				<p className="bk-settings__retire-line">
					It stops being offered anywhere an account is picked. Nothing already posted to it changes, and you can bring it back
					from <strong>Show retired accounts</strong>.
				</p>
				<p className="bk-settings__retire-line bk-settings__retire-line--muted">
					Any payees remembered against this account stop being applied while it is away, so next period's payees are not sent to
					an account nobody can pick. Bring the account back and they apply again.
				</p>
			</ToolsModal>
		);
	};

	renderRetiredAccounts = () => {
		const { retiredAccounts, showRetiredAccounts, onToggleShowRetiredAccounts, onRestoreAccount } = this.props;
		if (!onToggleShowRetiredAccounts) return null;

		const retired = retiredAccounts || [];

		return (
			<div className="bk-settings__retired">
				<button type="button" className="tk-text-action tk-text-action--muted" onClick={onToggleShowRetiredAccounts}>
					{showRetiredAccounts ? "Hide retired accounts" : "Show retired accounts"}
				</button>
				{showRetiredAccounts && retired.length === 0 && (
					<p className="bk-settings__retired-empty">No accounts have been put away.</p>
				)}
				{showRetiredAccounts && retired.map(account => (
					<div key={account.id} className="bk-settings__retired-row">
						<span className="bk-settings__retired-name">{BookkeepingService.describeChartAccount({ account })}</span>
						<button type="button" className="tk-text-action" onClick={() => onRestoreAccount({ account })}>Bring it back</button>
					</div>
				))}
			</div>
		);
	};


	renderBankPostingNote = ({ account, isLinked, isSaved }) => {
		if (!isSaved) {
			return (
				<span className="bk-settings__bank-posting-note bk-settings__bank-posting-note--unlinked">
					<Icon.AlertTriangle size={12} />
					Not on the file yet — save the import first
				</span>
			);
		}
		if (isLinked) return <span className="bk-settings__bank-posting-note">{account.canonicalLabel || account.canonicalCode}</span>;
		return (
			<span className="bk-settings__bank-posting-note bk-settings__bank-posting-note--unlinked">
				<Icon.AlertTriangle size={12} />
				Not linked — this account's money has nowhere to land
			</span>
		);
	};

	renderBankAccountRow = account => {
		const { onSetAccountCurrency, onFlipAccountSigns, checkingFlipFor, getBankAccountSignSummary, chartAccountOptions, onSetBankAccountChartAccount } = this.props;

		const signSummary = getBankAccountSignSummary ? getBankAccountSignSummary(account.accountName) : null;
		const signDescription = signSummary
			? `${signSummary.moneyInCount} in · ${signSummary.moneyOutCount} out · net ${UtilityService.formatCurrency(signSummary.netAmount)}`
			: "";

		const currencyOptions = CURRENCIES.map(currency => ({ value: currency.code, label: currency.code + " — " + currency.name }));
		const isLinked = !!account.accountId;
		const isSaved = !!account.id;
		const isCheckingFlip = isSaved && checkingFlipFor === account.id;

		return (
			<div key={account.accountName} className="bk-settings__bank-row">
				<div className="bk-settings__bank-line">
					<div className="bk-settings__bank-identity">
						<div className="bk-settings__bank-name">{account.accountName}</div>
						{signDescription && <div className="bk-settings__bank-signs">{signDescription}</div>}
					</div>
					<ToolsSelect
						inline
						className="tools-select--compact"
						value={account.currency || "CAD"}
						options={currencyOptions}
						onChange={currency => onSetAccountCurrency({ accountName: account.accountName, currency })}
					/>
					<button
						type="button"
						className="tk-button tk-button--secondary"
						disabled={isCheckingFlip}
						onClick={() => onFlipAccountSigns({ accountName: account.accountName, bankAccountId: account.id })}
						title="Reverse money in and money out for this account"
					>
						<Icon.Repeat size={14} />
						{isCheckingFlip ? "Checking…" : "Flip signs"}
					</button>
				</div>

				<div className="bk-settings__bank-line bk-settings__bank-line--posting">
					<span className="bk-settings__bank-posting-label">Posts to</span>
					<ToolsSelect
						inline
						searchable
						className="tools-select--account-picker"
						value={account.accountId || ""}
						options={chartAccountOptions || []}
						placeholder="Choose an account…"
						disabled={!isSaved}
						onChange={accountId => onSetBankAccountChartAccount({ bankAccountId: account.id, accountId })}
					/>
					{this.renderBankPostingNote({ account, isLinked, isSaved })}
				</div>
			</div>
		);
	};

	renderFlipSignsWarningLine = line => (
		<p key={line} className="bank-review__confirm-line">{line}</p>
	);

	renderFlipSignsWarning = () => {
		const { flipSignsToConfirm, flippingSigns, onFlipSignsConfirmed, onFlipSignsCancelled } = this.props;
		if (!flipSignsToConfirm) return null;

		const { words } = flipSignsToConfirm;

		const footer = (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" disabled={flippingSigns} onClick={onFlipSignsCancelled}>Keep as it is</button>
					<button type="button" className="tk-button tk-button--danger" disabled={flippingSigns} onClick={onFlipSignsConfirmed}>
						{flippingSigns ? "Flipping…" : "Flip anyway"}
					</button>
				</span>
			</>
		);

		return (
			<ToolsModal open={true} onClose={onFlipSignsCancelled} title={words.title} width={520} footer={footer}>
				{words.lines.map(this.renderFlipSignsWarningLine)}
				{words.note && <p className="bank-review__confirm-line bank-review__confirm-line--muted">{words.note}</p>}
			</ToolsModal>
		);
	};

	renderBankAccountsTab = () => {
		const accounts = this.props.bankAccounts || [];
		if (accounts.length === 0) {
			return <div className="bk-settings__empty">No bank accounts yet — import statements first.</div>;
		}
		return (
			<div>
				{accounts.map(this.renderBankAccountRow)}
				<p className="bk-settings__bank-hint">Changing a currency re-converts that account's transactions to CAD at the Bank of Canada rate for each date. A non-CAD account is needed for cross-currency transfers to be detected.</p>
				<p className="bk-settings__bank-hint">Flip signs when a statement imported the wrong way round — every deposit becomes a payment and every payment a deposit. Flipping again puts it back. Classifications are left alone, so review them afterwards if amounts changed direction.</p>
			</div>
		);
	};

	renderAccountsTab = filteredNames => {
		const { accountNames, getCanonicalLabelForAccount, autoMappingAccounts, onAutoMapAccounts, onOpenAddAccount, fsMappingReferenceLoaded } = this.props;

		const unmappedCount = fsMappingReferenceLoaded ? accountNames.filter(name => !getCanonicalLabelForAccount(name)).length : 0;

		return (
			<>
				{unmappedCount > 0 && (
					<div className="bk-settings__notice">
						<span className="bk-settings__notice-text">{unmappedCount} account{unmappedCount > 1 ? "s" : ""} not mapped to an FS account.</span>
						<button type="button" className="tk-button tk-button--secondary" onClick={onAutoMapAccounts} disabled={autoMappingAccounts}>
							{autoMappingAccounts ? <Icon.Loader size={14} /> : <Icon.Zap size={14} />}
							{autoMappingAccounts ? "Mapping…" : "Auto-map with AI"}
						</button>
					</div>
				)}
				<div className="bk-settings__add-row">
					<button type="button" className="tk-button tk-button--secondary" onClick={onOpenAddAccount}>
						<Icon.Plus size={14} />
						Add chart account
					</button>
				</div>
				<div className="bk-settings__list-header">
					<span>Account (edit to rename)</span>
					<span>FS account mapping, and the statement it lands on</span>
				</div>
				<div className="bk-settings__list">
					{filteredNames.map(this.renderAccountRow)}
				</div>
				{this.renderRetiredAccounts()}
				{this.renderRetireConfirmation()}
			</>
		);
	};

	renderTaxSetupForATrialBalanceFile = () => {
		const { taxSetup, taxSetupLoading, taxSetupError } = this.state;
		return (
			<div className="bk-settings__trial-balance-taxes" data-test="taxes-for-a-trial-balance">
				<div className="tk-notice">
					<p className="tk-notice__detail">
						This file's books are a trial balance, so there are no transactions to tax. The per-account tax defaults the
						bookkeeping uses to split GST/HST out of bank transactions do not apply here: the GST/HST on the statements is the
						balance of the GST/HST accounts in the books, and the review checks it against QuickBooks.
					</p>
				</div>
				{taxSetupLoading && <p className="bk-settings__muted">Reading QuickBooks's tax setup…</p>}
				{taxSetupError && <p className="bk-settings__problem">{taxSetupError}</p>}
				{taxSetup && taxSetup.booksCameFromQuickBooks && !taxSetup.available && <p className="bk-settings__muted">{taxSetup.reasonNotAvailable}</p>}
				{taxSetup && taxSetup.available && (
					<>
						<div className="bk-settings__list-header bk-settings__qbo-tax-code-head">
							<span>QuickBooks tax code</span>
							<span>On sales</span>
							<span>On purchases</span>
						</div>
						<div className="bk-settings__list">
							{taxSetup.taxCodes.length === 0 && <p className="bk-settings__muted">QuickBooks has no tax codes for this company.</p>}
							{taxSetup.taxCodes.map(code => (
								<div key={code.id} className="bk-settings__qbo-tax-code">
									<span className="bk-settings__tax-name">{code.name}{!code.active && <span className="bk-settings__muted"> · inactive</span>}</span>
									<span>{BookkeepingService.describeQuickBooksTaxRates({ rates: code.salesRates })}</span>
									<span>{BookkeepingService.describeQuickBooksTaxRates({ rates: code.purchaseRates })}</span>
								</div>
							))}
						</div>
						<div className="bk-settings__list-header bk-settings__list-header--spaced">
							<span>Account</span>
							<span>QuickBooks default tax code</span>
						</div>
						<div className="bk-settings__list">
							{taxSetup.accountDefaults.map(account => (
								<div key={[account.accountNumber, account.accountName].join("|")} className="bk-settings__tax-row">
									<span className="bk-settings__tax-name">{BookkeepingService.describeChartAccount({ account })}</span>
									<span>{account.taxCodeName}</span>
								</div>
							))}
						</div>
						<p className="bk-settings__muted">
							{UtilityService.describeCount({ count: taxSetup.accountsWithoutADefault, singular: "other account has", plural: "other accounts have" })} no default in QuickBooks. A default only pre-fills a transaction entered in QuickBooks; it is not what was charged.
						</p>
					</>
				)}
			</div>
		);
	};

	renderTaxesTab = filteredNames => {
		if (this.props.booksAreATrialBalance) return this.renderTaxSetupForATrialBalanceFile();
		const { classificationTaxMap, transactions, customTaxRates, taxSettingsLoading, onFetchAllTaxCodes } = this.props;

		const effectiveTaxMap = { ...classificationTaxMap };
		for (let t of (transactions || [])) {
			if (t.classification && t.tax && !effectiveTaxMap[t.classification]) {
				effectiveTaxMap[t.classification] = t.tax;
			}
		}

		const everyProvinceTaxLists = Object.values(TAX_CATEGORIES);
		const everyProvinceTax = everyProvinceTaxLists.flat();
		const taxesInUse = filteredNames.map(name => effectiveTaxMap[name]).filter(Boolean);
		const allTaxChoices = [...everyProvinceTax, ...(customTaxRates || []), ...taxesInUse];
		const uniqueTaxChoices = [...new Set(allTaxChoices)];

		const hasMissing = filteredNames.some(name => !effectiveTaxMap[name]);

		return (
			<>
				{hasMissing && (
					<div className="bk-settings__notice">
						<span className="bk-settings__notice-text">Some accounts have no tax assigned.</span>
						<button type="button" className="tk-button tk-button--secondary" onClick={onFetchAllTaxCodes} disabled={taxSettingsLoading}>
							{taxSettingsLoading ? <Icon.Loader size={14} /> : <Icon.Zap size={14} />}
							{taxSettingsLoading ? "Fetching…" : "Fetch all tax codes"}
						</button>
					</div>
				)}
				<div className="bk-settings__list-header">
					<span>Account</span>
					<span>Tax</span>
				</div>
				<div className="bk-settings__list">
					{filteredNames.map(name => (
						<div key={name} className="bk-settings__tax-row-wrap">
							<div className="bk-settings__tax-row">
								<span className="bk-settings__tax-name">{name}</span>
								<select
									className="tk-select bk-settings__tax-select"
									value={effectiveTaxMap[name] || ""}
									onChange={e => this.onAccountTaxSelect(name, e.target.value)}
								>
									<option value="">— No default —</option>
									{uniqueTaxChoices.map(tax => (
										<option key={tax} value={tax}>{tax}</option>
									))}
									<option value={CUSTOM_RATE_OPTION_VALUE}>+ Custom rate…</option>
								</select>
							</div>
							{this.state.customRateForAccount === name && (
								<CustomTaxRateRow
									clientId={this.props.clientId}
									taxOptions={this.props.taxOptions}
									customTaxRates={this.props.customTaxRates}
									onCustomTaxRatesChange={this.props.onCustomTaxRatesChange}
									onApply={this.onCustomRateAppliedToAccount}
									onCancel={this.onCancelCustomRateForAccount}
								/>
							)}
						</div>
					))}
				</div>
				{this.renderCustomTaxRatesSection()}
				{this.renderTaxRulesSection()}
			</>
		);
	};

	renderCustomTaxRateChip = rate => {
		const { editingTaxRate } = this.state;
		const isBeingEdited = editingTaxRate === rate;

		return (
			<span key={rate} className={clsx("bk-settings__custom-rate-chip", isBeingEdited && "bk-settings__custom-rate-chip--editing")}>
				<button type="button" className="bk-settings__custom-rate-name" title="Edit this rate" onClick={() => this.onStartEditingTaxRate(rate)}>
					{rate}
				</button>
				<button type="button" className="bk-settings__custom-rate-remove" title="Remove this rate" onClick={() => this.onRemoveCustomTaxRate(rate)}>
					<Icon.X size={12} />
				</button>
			</span>
		);
	};

	onAddTaxRule = () => {
		const { taxRules, onTaxRulesChange } = this.props;
		const keyword = this.state.newRuleKeyword.trim();
		const tax = this.state.newRuleTax;
		if (!keyword || !tax) return;

		const updatedRules = [...(taxRules || []), { keyword, tax }];
		onTaxRulesChange(updatedRules);
		this.update({ newRuleKeyword: "", newRuleTax: "" });
	};

	onRemoveTaxRule = removedIndex => {
		const { taxRules, onTaxRulesChange } = this.props;
		const updatedRules = (taxRules || []).filter((rule, ruleIndex) => ruleIndex !== removedIndex);
		onTaxRulesChange(updatedRules);
	};

	renderTaxRuleRow = (rule, index) => {
		return (
			<div key={index} className="bk-settings__tax-row">
				<span className="bk-settings__tax-name"><strong>{rule.keyword}</strong> → {rule.tax}</span>
				<button type="button" className="bk-settings__custom-rate-remove" title="Remove rule" onClick={() => this.onRemoveTaxRule(index)}>
					<Icon.X size={14} />
				</button>
			</div>
		);
	};

	renderTaxRulesSection = () => {
		const { taxRules, taxOptions, onApplyTaxRules } = this.props;
		const { newRuleKeyword, newRuleTax } = this.state;

		const taxChoices = taxOptions.map(taxOption => ({ value: taxOption, label: taxOption }));
		const canAddRule = newRuleKeyword.trim() !== "" && newRuleTax !== "";
		const hasRules = (taxRules || []).length > 0;

		return (
			<div className="bk-settings__tax-rules">
				<div className="bk-settings__list-header">
					<span>Keyword tax rules</span>
				</div>
				<p className="bk-settings__section-hint">Any transaction whose description or merchant contains the keyword gets this tax. Whole-word, case-insensitive.</p>
				{hasRules && <div className="bk-settings__list">{taxRules.map(this.renderTaxRuleRow)}</div>}
				<div className="bk-settings__add-row">
					<input
						className="tk-input"
						placeholder="Keyword (e.g. usa)…"
						value={newRuleKeyword}
						onChange={e => this.update({ newRuleKeyword: e.target.value })}
					/>
					<div className="bk-settings__rule-tax">
						<ToolsSelect
							value={newRuleTax}
							options={taxChoices}
							onChange={selectedTax => this.update({ newRuleTax: selectedTax })}
							placeholder="Tax…"
						/>
					</div>
					<button type="button" className="tk-button tk-button--secondary" onClick={this.onAddTaxRule} disabled={!canAddRule}>
						<Icon.Plus size={14} />
						Add rule
					</button>
				</div>
				{hasRules && (
					<div className="bk-settings__tax-rules-apply">
						<button type="button" className="tk-button tk-button--secondary" onClick={onApplyTaxRules}>
							<Icon.Zap size={14} />
							Apply to untaxed transactions
						</button>
					</div>
				)}
			</div>
		);
	};

	renderCustomTaxRatesSection = () => {
		const { customTaxRates } = this.props;
		const { newTaxRateName, newTaxRatePercent, editingTaxRate } = this.state;

		return (
			<div className="bk-settings__custom-rates">
				<div className="bk-settings__list-header">
					<span>Custom tax rates{customTaxRates.length > 0 ? " (click one to edit)" : ""}</span>
				</div>
				{customTaxRates.length > 0 && (
					<div className="bk-settings__custom-rate-chips">
						{customTaxRates.map(this.renderCustomTaxRateChip)}
					</div>
				)}
				<div className="bk-settings__add-row">
					<input
						className="tk-input"
						list="bk-settings-tax-name-options"
						placeholder="Tax name (e.g. PST, GST, HST)…"
						value={newTaxRateName}
						onChange={e => this.update({ newTaxRateName: e.target.value })}
					/>
					<datalist id="bk-settings-tax-name-options">
						{COMMON_TAX_NAMES.map(name => (
							<option key={name} value={name} />
						))}
					</datalist>
					<input
						className="tk-input bk-settings__rate-input"
						type="number"
						step="0.01"
						min="0"
						max="100"
						placeholder="%"
						value={newTaxRatePercent}
						onChange={e => this.update({ newTaxRatePercent: e.target.value })}
					/>
					<button type="button" className="tk-button tk-button--secondary" onClick={this.onAddCustomTaxRate} disabled={newTaxRatePercent === "" || !newTaxRateName.trim()}>
						{editingTaxRate ? <Icon.Check size={14} /> : <Icon.Plus size={14} />}
						{editingTaxRate ? "Update rate" : "Add rate"}
					</button>
					{editingTaxRate && (
						<button type="button" className="tk-button tk-button--secondary" onClick={this.onCancelEditingTaxRate}>Cancel</button>
					)}
				</div>
			</div>
		);
	};

	render() {
		const { accountNames, onClose } = this.props;
		const { activeTab, searchText } = this.state;

		const filteredNames = searchText
			? accountNames.filter(name => name.toLowerCase().includes(searchText.toLowerCase()))
			: accountNames;

		const tabs = [
			{ key: "accounts", label: "Accounts" },
			{ key: "taxes", label: "Taxes" },
			{ key: "currencies", label: "Bank accounts" },
			{ key: "columns", label: "Columns" },
			{ key: "period", label: "Fiscal period" },
			{ key: "prior-year", label: "Prior year" },
		];

		const footer = (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={onClose}>Close</button>
				</span>
			</>
		);

		return (
			<ToolsModal open={true} onClose={onClose} title="Settings" width={880} footer={footer}>
				{this.renderFlipSignsWarning()}
				<ToolsTabs tabs={tabs} activeKey={activeTab} onSelect={this.onTabSelect} />

				<div className="bk-settings__body">
					{activeTab !== "currencies" && activeTab !== "period" && activeTab !== "columns" && activeTab !== "prior-year" && !(activeTab === "taxes" && this.props.booksAreATrialBalance) && (
						<div className="tk-search bk-settings__search">
							<Icon.Search size={15} className="tk-search__icon" />
							<input
								className="tk-search__input"
								placeholder="Search accounts…"
								value={searchText}
								onChange={e => this.update({ searchText: e.target.value })}
							/>
						</div>
					)}
					{activeTab === "accounts" && this.renderAccountsTab(filteredNames)}
					{activeTab === "taxes" && this.renderTaxesTab(filteredNames)}
					{activeTab === "currencies" && this.renderBankAccountsTab()}
					{activeTab === "columns" && this.renderColumnsTab()}
					{activeTab === "period" && this.renderFiscalPeriodTab()}
					{activeTab === "prior-year" && this.renderPriorYearTab()}
				</div>
			</ToolsModal>
		);
	}
}

export default SettingsModal;
